import React, { useEffect, useRef, useState, useCallback } from 'react';

/* Assistant IA admin — chat embarqué (Hermes AIAgent côté backend).
   Filet git : après chaque tour, le diff est affiché avec un bouton Annuler. */

type Msg =
  | { role: 'user'; text: string }
  | { role: 'assistant'; text: string; reasoning?: string; tools?: string[]; diff?: string; ref?: string; reverted?: boolean };

type ConversationSummary = {
  id: string;
  title: string;
  created_at?: string;
  updated_at?: string;
  model?: string;
  message_count?: number;
};

const WS_BASE = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;
const HTTP_BASE = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;

function TypingDots() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 18 }} aria-label="L'assistant écrit">
      <style>{`@keyframes agentTypingDot { 0%, 80%, 100% { transform: translateY(0); opacity: .45; } 40% { transform: translateY(-5px); opacity: 1; } }`}</style>
      {[0, 1, 2].map(i => (
        <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#7c3aed', display: 'inline-block', animation: 'agentTypingDot 1.1s infinite ease-in-out', animationDelay: `${i * 0.16}s` }} />
      ))}
    </span>
  );
}

export default function AgentChat({ inline = false }: { inline?: boolean }) {
  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [model, setModel] = useState('');
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [attach, setAttach] = useState<{ path: string; name: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pendingPayloadsRef = useRef<Record<string, unknown>[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollDown = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= 1) return;
    const token = localStorage.getItem('nslive_token') || '';
    const ws = new WebSocket(`${WS_BASE}/admin/agent/ws?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      const pending = pendingPayloadsRef.current.splice(0);
      pending.forEach(payload => ws.send(JSON.stringify(payload)));
    };
    ws.onclose = () => { setConnected(false); setBusy(false); };
    ws.onerror = () => setConnected(false);
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data);
      if (m.type === 'ready') {
        setModels(m.models || []);
        setModel(m.model || '');
        setConversations(m.conversations || []);
        setCurrentConversationId(m.current_id || null);
        return;
      }
      if (m.type === 'conversations') {
        setConversations(m.conversations || []);
        setCurrentConversationId(m.current_id || null);
        return;
      }
      if (m.type === 'conversation_loaded') {
        setMsgs(m.messages || []);
        setCurrentConversationId(m.current_id || null);
        setAttach(null);
        setBusy(false);
        if (m.model) setModel(m.model);
        setHistoryOpen(false);
        scrollDown();
        return;
      }
      if (m.type === 'model') { setModel(m.model); return; }
      if (m.type === 'turn_start') {
        setMsgs(p => [...p, { role: 'assistant', text: '', tools: [] }]);
        scrollDown(); return;
      }
      if (m.type === 'text_delta') {
        const chunk = typeof m.text === 'string' && m.text !== 'null' ? m.text : '';
        if (!chunk) return;
        setMsgs(p => { const c = [...p]; const last = c[c.length - 1]; if (last && last.role === 'assistant') last.text += chunk; return c; });
        scrollDown(); return;
      }
      if (m.type === 'reasoning') {
        const chunk = typeof m.text === 'string' && m.text !== 'null' ? m.text : '';
        if (!chunk) return;
        setMsgs(p => { const c = [...p]; const last = c[c.length - 1]; if (last && last.role === 'assistant') last.reasoning = (last.reasoning || '') + chunk; return c; });
        return;
      }
      if (m.type === 'tool') {
        if (m.phase === 'start') setMsgs(p => { const c = [...p]; const last = c[c.length - 1]; if (last && last.role === 'assistant') last.tools = [...(last.tools || []), m.name]; return c; });
        scrollDown(); return;
      }
      if (m.type === 'turn_done') {
        setMsgs(p => { const c = [...p]; const last = c[c.length - 1]; if (last && last.role === 'assistant') { if (m.final) last.text = m.final; last.diff = m.diff || ''; last.ref = m.ref_before || ''; } return c; });
        if (m.conversation_id) setCurrentConversationId(m.conversation_id);
        setBusy(false); scrollDown(); return;
      }
      if (m.type === 'reverted') {
        setMsgs(p => { const c = [...p]; for (let i = c.length - 1; i >= 0; i--) { const x = c[i]; if (x.role === 'assistant' && x.diff) { x.reverted = m.ok; break; } } return c; });
        return;
      }
      if (m.type === 'error') {
        setMsgs(p => [...p, { role: 'assistant', text: '⚠️ ' + m.msg }]);
        setBusy(false); scrollDown(); return;
      }
    };
  }, [scrollDown]);

  useEffect(() => { if (open) connect(); }, [open, connect]);
  useEffect(() => () => { wsRef.current?.close(); }, []);

  const send = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1 || busy) return;
    const text = input.trim();
    if (!text && !attach) return;
    setMsgs(p => [...p, { role: 'user', text: text + (attach ? `\n📎 ${attach.name}` : '') }]);
    ws.send(JSON.stringify({ type: 'user', text, image_path: attach?.path || null }));
    setInput(''); setAttach(null); setBusy(true); scrollDown();
  };

  const doRevert = (ref: string) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1 && ref) ws.send(JSON.stringify({ type: 'revert', ref }));
  };

  const changeModel = (m: string) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: 'set_model', model: m }));
  };

  const sendWs = (payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    pendingPayloadsRef.current.push(payload);
    connect();
    return false;
  };

  const newConversation = () => sendWs({ type: 'new_conversation' });
  const loadConversation = (id: string) => sendWs({ type: 'load_conversation', id });
  const renameConversation = (id: string, currentTitle: string) => {
    const title = window.prompt('Titre de la conversation', currentTitle || 'Nouvelle conversation')?.trim();
    if (title) sendWs({ type: 'rename_conversation', id, title });
  };
  const deleteConversation = (id: string, title: string) => {
    if (window.confirm(`Supprimer la conversation « ${title} » ?`)) {
      sendWs({ type: 'delete_conversation', id });
    }
  };

  const formatConversationDate = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const currentConversation = conversations.find(c => c.id === currentConversationId);

  const upload = async (f: File) => {
    const token = localStorage.getItem('nslive_token') || '';
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await fetch(`${HTTP_BASE}/admin/agent/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (r.ok) { const j = await r.json(); setAttach({ path: j.path, name: j.name }); }
    } catch { /* ignore */ }
  };

  const uploadPastedImage = async (file: File) => {
    const ext = file.type.split('/')[1]?.split(';')[0] || 'png';
    const name = file.name || `image-collee-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`;
    const image = file.name ? file : new File([file], name, { type: file.type || 'image/png' });
    await upload(image);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (busy) return;
    const files = Array.from(e.clipboardData.files || []);
    const fileImage = files.find(f => f.type.startsWith('image/'));
    if (fileImage) {
      e.preventDefault();
      uploadPastedImage(fileImage);
      return;
    }

    const itemImage = Array.from(e.clipboardData.items || [])
      .find(item => item.type.startsWith('image/'));
    const pastedFile = itemImage?.getAsFile();
    if (pastedFile) {
      e.preventDefault();
      uploadPastedImage(pastedFile);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Assistant IA"
        style={inline
          ? { width: 42, height: 42, borderRadius: 10, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }
          : { position: 'fixed', top: 16, right: 16, zIndex: 1000, width: 46, height: 46, borderRadius: 23, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', boxShadow: '0 4px 14px rgba(124,58,237,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2a7 7 0 0 1 7 7v1a7 7 0 0 1-7 7H9l-4 4v-4a7 7 0 0 1-2-5V9a7 7 0 0 1 7-7z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" /></svg>
      </button>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px,100vw)', zIndex: 1000, background: '#fff', borderLeft: '1px solid #e2e8f0', boxShadow: '-8px 0 24px rgba(15,23,42,0.12)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ flexShrink: 0, padding: '12px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: connected ? '#16a34a' : '#dc2626' }} />
        <strong style={{ fontSize: 14, color: '#0f172a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={currentConversation?.title || 'Assistant IA'}>
          {currentConversation?.title || 'Assistant IA'}
        </strong>
        <button onClick={() => setHistoryOpen(v => !v)} title="Historique des conversations"
          style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: historyOpen ? '#ede9fe' : '#f8fafc', color: '#0f172a', cursor: 'pointer' }}>
          Historique
        </button>
        <select value={model} onChange={e => { setModel(e.target.value); changeModel(e.target.value); }}
          style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#0f172a', maxWidth: 115 }}>
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: '#64748b', lineHeight: 1 }}>×</button>
        {historyOpen && (
          <div style={{ position: 'absolute', top: 48, right: 42, width: 330, maxWidth: 'calc(100vw - 70px)', maxHeight: 420, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 12px 32px rgba(15,23,42,0.18)', zIndex: 1001, padding: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 13, color: '#0f172a' }}>Historique</strong>
              <button onClick={newConversation} disabled={busy} style={{ fontSize: 12, border: 'none', borderRadius: 6, background: busy ? '#cbd5e1' : '#7c3aed', color: '#fff', padding: '5px 8px', cursor: busy ? 'not-allowed' : 'pointer' }}>+ Nouveau</button>
            </div>
            {conversations.length === 0 ? (
              <div style={{ fontSize: 12, color: '#94a3b8', padding: '14px 4px', textAlign: 'center' }}>Aucune conversation enregistrée.</div>
            ) : conversations.map(c => (
              <div key={c.id} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '7px 0', borderTop: '1px solid #f1f5f9' }}>
                <button onClick={() => loadConversation(c.id)} disabled={busy}
                  style={{ flex: 1, textAlign: 'left', border: 'none', background: c.id === currentConversationId ? '#f3e8ff' : 'transparent', borderRadius: 8, padding: '7px 8px', cursor: busy ? 'not-allowed' : 'pointer', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || 'Nouvelle conversation'}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{formatConversationDate(c.updated_at)} · {c.message_count || 0} messages</div>
                </button>
                <button onClick={() => renameConversation(c.id, c.title)} title="Renommer" disabled={busy} style={{ border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', color: '#64748b', fontSize: 14 }}>✎</button>
                <button onClick={() => deleteConversation(c.id, c.title)} title="Supprimer" disabled={busy} style={{ border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', color: '#dc2626', fontSize: 15 }}>🗑</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12, background: '#f8fafc' }}>
        {msgs.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 30 }}>
            Dans ce chat, que peut on faire exactement ?
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '92%' }}>
            <div style={{ padding: '8px 11px', borderRadius: 10, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: m.role === 'user' ? '#7c3aed' : '#fff', color: m.role === 'user' ? '#fff' : '#0f172a', border: m.role === 'user' ? 'none' : '1px solid #e2e8f0' }}>
              {m.role === 'assistant' && (m as any).tools?.length > 0 && (
                <div style={{ fontSize: 11, color: '#7c3aed', marginBottom: 5 }}>🔧 {(m as any).tools.join(', ')}</div>
              )}
              {m.text || (m.role === 'assistant' && busy && i === msgs.length - 1 ? <TypingDots /> : '')}
            </div>
            {m.role === 'assistant' && (m as any).reasoning && (
              <details style={{ marginTop: 4 }}>
                <summary style={{ fontSize: 11, color: '#94a3b8', cursor: 'pointer' }}>raisonnement</summary>
                <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'pre-wrap', marginTop: 4 }}>{(m as any).reasoning}</div>
              </details>
            )}
            {m.role === 'assistant' && (m as any).diff && (
              <div style={{ marginTop: 6, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                <details>
                  <summary style={{ fontSize: 12, padding: '6px 10px', background: '#fef3c7', color: '#92400e', cursor: 'pointer', fontWeight: 600 }}>
                    Modifications appliquées — voir le diff
                  </summary>
                  <pre style={{ margin: 0, padding: 10, fontSize: 11, lineHeight: 1.4, background: '#0f172a', color: '#e2e8f0', overflowX: 'auto', maxHeight: 320 }}>{(m as any).diff}</pre>
                </details>
                <div style={{ padding: 8, display: 'flex', alignItems: 'center', gap: 8, background: '#fff' }}>
                  {(m as any).reverted === true
                    ? <span style={{ fontSize: 12, color: '#16a34a' }}>✓ Changements annulés</span>
                    : (m as any).reverted === false
                      ? <span style={{ fontSize: 12, color: '#dc2626' }}>Échec de l'annulation</span>
                      : <button onClick={() => doRevert((m as any).ref)} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>Annuler ces changements</button>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input */}
      <div style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0', padding: 10, background: '#fff' }}>
        {attach && (
          <div style={{ fontSize: 12, color: '#7c3aed', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            📎 {attach.name}
            <button onClick={() => setAttach(null)} style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer' }}>×</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <label style={{ cursor: 'pointer', padding: '8px', color: '#64748b', flexShrink: 0 }} title="Joindre un fichier">
            📎
            <input type="file" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          </label>
          <textarea value={input} onChange={e => setInput(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={busy ? 'Tour en cours…' : 'Demande une modification… colle une image ici avec Ctrl+V'} disabled={busy} rows={2}
            style={{ flex: 1, resize: 'none', fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontFamily: 'inherit', outline: 'none' }} />
          <button onClick={send} disabled={busy || (!input.trim() && !attach)}
            style={{ flexShrink: 0, padding: '9px 14px', borderRadius: 8, border: 'none', cursor: busy ? 'not-allowed' : 'pointer', background: busy ? '#cbd5e1' : 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', fontWeight: 600, fontSize: 13 }}>
            ▸
          </button>
        </div>
      </div>
    </div>
  );
}
